import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '@/contexts/LanguageContext';
import api from '@/utils/api';
import ScrollReveal from '@/components/effects/ScrollReveal';
import { PlayCircle, Users, Clock, Sparkles, Shield, Award, Flame } from 'lucide-react';
import UpgradeAccountBanner from '@/components/UpgradeAccountBanner';

const LEVEL_LABELS = {
  beginner: 'Seeker',
  intermediate: 'Thinker',
  advanced: 'Builder',
  master: 'Sovereign',
};

export default function Courses() {
  const { t, localizedField } = useLang();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/courses').then(r => { setCourses(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const totalDuration = (episodes) => {
    if (!episodes?.length) return 0;
    return episodes.reduce((sum, ep) => sum + (ep.duration_minutes || 0), 0);
  };

  const hasIntro = (c) => !!(c.intro_video_id || c.intro_video_url);

  return (
    <div data-testid="courses-page" className="min-h-screen">
      <UpgradeAccountBanner />
      <section className="pt-12 pb-8 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <ScrollReveal>
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-sacred-gold/10 border border-sacred-gold/20" data-testid="courses-hero-chip">
              <Sparkles size={12} className="text-sacred-gold" />
              <span className="text-xs font-body text-sacred-gold uppercase tracking-wider">Encrypted · Premium · Yours for life</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-display text-heading mb-4" data-testid="courses-title">{t('courses.title')}</h1>
            <p className="text-base text-text-muted font-body max-w-2xl mx-auto">
              Structured wisdom. Each course is a journey through the frameworks that shape presence, influence, and strategic thinking.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Trust strip */}
      <section className="pb-10 px-4">
        <div className="max-w-4xl mx-auto grid grid-cols-3 gap-4">
          <div className="text-center" data-testid="trust-encrypted">
            <Shield size={18} className="mx-auto text-sacred-gold/80 mb-1" />
            <p className="text-xs font-body text-text-muted">AES-128 encrypted</p>
          </div>
          <div className="text-center" data-testid="trust-certificate">
            <Award size={18} className="mx-auto text-sacred-gold/80 mb-1" />
            <p className="text-xs font-body text-text-muted">Level-tiered certificates</p>
          </div>
          <div className="text-center" data-testid="trust-lifetime">
            <Flame size={18} className="mx-auto text-sacred-gold/80 mb-1" />
            <p className="text-xs font-body text-text-muted">Lifetime access</p>
          </div>
        </div>
      </section>

      <section className="pb-24 px-4">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center text-text-muted font-body py-20" data-testid="courses-loading">{t('common.loading')}</div>
          ) : courses.length === 0 ? (
            <ScrollReveal>
              <div className="text-center py-20" data-testid="courses-empty">
                <div className="w-20 h-20 rounded-full bg-sacred-gold/10 flex items-center justify-center mx-auto mb-6">
                  <PlayCircle size={32} className="text-sacred-gold" />
                </div>
                <h2 className="text-2xl font-display text-heading mb-3">{t('courses.comingSoon')}</h2>
                <p className="text-text-muted font-body">New courses are being crafted. Stay in the orbit.</p>
              </div>
            </ScrollReveal>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {courses.map((course, i) => (
                <ScrollReveal key={course.id} delay={i * 0.1}>
                  <Link
                    to={`/courses/${course.slug}`}
                    className="group block glass-card rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                    data-testid={`course-card-${course.slug}`}
                  >
                    <div className="aspect-video bg-gradient-to-br from-sacred-gold/20 to-warm-brown/10 flex items-center justify-center relative overflow-hidden">
                      {course.thumbnail_url ? (
                        <img src={course.thumbnail_url} alt={localizedField(course.title)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <PlayCircle size={48} className="text-sacred-gold/40 group-hover:text-sacred-gold/70 transition-colors" />
                      )}
                      {hasIntro(course) && (
                        <div className="absolute bottom-3 left-3 rtl:left-auto rtl:right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-black/70 text-sacred-gold text-[10px] font-body backdrop-blur-sm" data-testid={`card-has-intro-${course.slug}`}>
                          <Sparkles size={10} /> Watch Trailer
                        </div>
                      )}
                      {!course.is_free && (
                        <div className="absolute top-3 right-3 rtl:right-auto rtl:left-3 px-3 py-1 rounded-full bg-heading/80 text-cream text-xs font-body backdrop-blur-sm">
                          ${course.price_usd}
                        </div>
                      )}
                      {course.is_free && (
                        <div className="absolute top-3 right-3 rtl:right-auto rtl:left-3 px-3 py-1 rounded-full bg-accent-success/80 text-cream text-xs font-body backdrop-blur-sm">
                          Free
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-body uppercase tracking-wider text-sacred-gold/70" data-testid={`card-level-${course.slug}`}>
                          {LEVEL_LABELS[course.level || 'beginner']}
                        </span>
                        {(course.enrollment_count || 0) >= 3 && (
                          <span className="text-[10px] font-body text-text-muted">· {course.enrollment_count} enrolled</span>
                        )}
                      </div>
                      <h3 className="font-display text-lg text-heading mb-2 group-hover:text-sacred-gold transition-colors" data-testid={`course-title-${course.slug}`}>
                        {localizedField(course.title)}
                      </h3>
                      <p className="text-sm text-text-muted font-body mb-4 line-clamp-2">
                        {localizedField(course.description)}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-dust-gray font-body">
                        <span className="flex items-center gap-1"><PlayCircle size={13} /> {course.episodes?.length || 0} episodes</span>
                        <span className="flex items-center gap-1"><Clock size={13} /> {totalDuration(course.episodes)}m</span>
                        <span className="flex items-center gap-1"><Users size={13} /> {course.enrollment_count || 0}</span>
                      </div>
                    </div>
                  </Link>
                </ScrollReveal>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
